import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './RequestReviewers.css';

const RequestReviewers = () => {
  const [requestedReviewers, setRequestedReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchRequestedReviewers();
  }, []);

  const fetchRequestedReviewers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/requested-reviewers`);
      setRequestedReviewers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requested reviewers:', error);
      setError('Failed to load reviewer requests. Please try again.');
      setLoading(false);
    }
  };

  const handleAccept = async (userId) => {
    const result = await Swal.fire({
      title: 'Accept Request?',
      text: 'Are you sure you want to accept this reviewer request?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#5e71f6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, accept',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setProcessingId(userId);
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/accept-request`, { userId });

        setProcessingId(null);

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Request Accepted',
            text: 'The reviewer request has been successfully accepted.',
            confirmButtonColor: '#5e71f6',
          });
          setRequestedReviewers((prevReviewers) =>
            prevReviewers.map((reviewer) =>
              reviewer._id === userId ? { ...reviewer, isApproved: true, reviwerrequestStatus: 'accepted' } : reviewer
            )
          );
        }
      } catch (error) {
        setProcessingId(null);
        console.error('Error accepting reviewer:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to accept reviewer request.',
          confirmButtonColor: '#5e71f6',
        });
      }
    }
  };

  const handleReject = async (userId) => {
    const result = await Swal.fire({
      title: 'Reject Request?',
      text: 'Are you sure you want to reject this reviewer request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#5e71f6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, reject',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setProcessingId(userId);
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/reject-request`, { userId });

        setProcessingId(null);

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Request Rejected',
            text: 'The reviewer request has been successfully rejected.',
            confirmButtonColor: '#5e71f6',
          });
          setRequestedReviewers((prevReviewers) =>
            prevReviewers.map((reviewer) =>
              reviewer._id === userId ? { ...reviewer, reviwerrequestStatus: 'rejected' } : reviewer
            )
          );
        }
      } catch (error) {
        setProcessingId(null);
        console.error('Error rejecting reviewer:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to reject reviewer request.',
          confirmButtonColor: '#5e71f6',
        });
      }
    }
  };

  const openDetailsModal = (reviewer) => {
    setSelectedReviewer(reviewer);
  };

  const closeDetailsModal = () => {
    setSelectedReviewer(null);
  };

  if (loading) {
    return (
      <div className="paper-list-wrapper">
        <div className="reviewers-container">
          <div className="header-section">
            <h1 className="paper-list-title">Requested Reviewers</h1>
            <p className="paper-list-subtitle">Review and manage reviewer access requests</p>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading reviewer requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="paper-list-wrapper">
        <div className="reviewers-container">
          <div className="header-section">
            <h1 className="paper-list-title">Requested Reviewers</h1>
            <p className="paper-list-subtitle">Review and manage reviewer access requests</p>
          </div>
          <div className="error-container">
            <div className="error-icon">!</div>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={fetchRequestedReviewers}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-list-wrapper">
      <div className="reviewers-container">
        <div className="header-section">
          <h1 className="paper-list-title">Requested Reviewers</h1>
          <p className="paper-list-subtitle">Review and manage reviewer access requests</p>
        </div>

        {requestedReviewers.length === 0 ? (
          <div className="no-reviewers-box">
            <svg className="no-data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="80" height="80">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3>No Requests Found</h3>
            <p>There are no reviewer access requests at the moment.</p>
          </div>
        ) : (
          <div className="reviewer-list">
            {requestedReviewers.map((reviewer) => {
              console.log("Reviewer request status: " + reviewer.reviwerrequestStatus);
              return (
                <div key={reviewer._id} className="reviewer-card">
                  <div className="reviewer-info">
                    <div className="reviewer-header">
                      <h3 className="reviewer-name">
                        {`${reviewer.firstName} ${reviewer.lastName}`}
                      </h3>
                      <span className={`status-badge ${reviewer.isApproved ? 'status-approved' : reviewer.reviwerrequestStatus === 'rejected' ? 'status-rejected' : 'status-pending'}`}>
                        {reviewer.isApproved ? 'Accepted' : reviewer.reviwerrequestStatus === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </div>
                    <div className="reviewer-email">
                      <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {reviewer.email}
                    </div>
                  </div>
                  <div className="action-buttons">
                    <button
                      className="btn btn-details"
                      onClick={() => openDetailsModal(reviewer)}
                    >
                      <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Details
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleAccept(reviewer._id)}
                      disabled={reviewer.isApproved || processingId === reviewer._id}
                    >
                      {processingId === reviewer._id ? (
                        <>
                          <div className="button-spinner"></div>
                          Processing...
                        </>
                      ) : reviewer.isApproved ? (
                        <>
                          <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Accepted
                        </>
                      ) : (
                        <>
                          <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Accept
                        </>
                      )}
                    </button>
                    <button
                      className={`btn ${reviewer.reviwerrequestStatus === 'rejected' ? 'btn-rejected' : 'btn-danger'}`}
                      onClick={() => handleReject(reviewer._id)}
                      disabled={reviewer.reviwerrequestStatus === 'rejected' || processingId === reviewer._id}
                    >
                      {processingId === reviewer._id ? (
                        <>
                          <div className="button-spinner"></div>
                          Processing...
                        </>
                      ) : reviewer.reviwerrequestStatus === 'rejected' ? (
                        <>
                          <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Rejected
                        </>
                      ) : (
                        <>
                          <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Reject
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedReviewer && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Reviewer Details</h3>
              <button className="close-button" onClick={closeDetailsModal}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{selectedReviewer.firstName} {selectedReviewer.lastName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{selectedReviewer.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Message:</span>
                <span className="detail-value">{selectedReviewer.message || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Domain 1:</span>
                <span className="detail-value">{selectedReviewer.domain1 || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Domain 2:</span>
                <span className="detail-value">{selectedReviewer.domain2 || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Domain 3:</span>
                <span className="detail-value">{selectedReviewer.domain3 || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">CV:</span>
                {selectedReviewer.cvUrl ? (
                  <button 
                    onClick={() => window.open(selectedReviewer.cvUrl, '_blank')} 
                    className="btn-show-cv"
                  >
                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View CV
                  </button>
                ) : (
                  <span className="detail-value not-available">Not Uploaded</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestReviewers;
